import React, { useState, useEffect } from "react";
import { X, Download, Share2 } from "lucide-react";
import { Button } from "./ui/button";

const isIOS = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  // iPad on iPadOS 13+ reports as Mac — detect via touch
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && "ontouchend" in document)
  );
};

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed || isStandalone()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari doesn't fire beforeinstallprompt — show a hint instead after
    // a short delay so we don't blast a brand-new visitor on first paint.
    let iosTimer;
    if (isIOS() && !isStandalone()) {
      iosTimer = setTimeout(() => setIosHint(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem("pwa-install-dismissed", "true");
  };

  if (!show && !iosHint) return null;

  if (iosHint && !show) {
    // iOS variant — instructional, not a one-tap install
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-80 z-[60] animate-in slide-in-from-bottom-4 duration-300"
        data-testid="ios-install-hint"
      >
        <div className="bg-slate-900 text-white rounded-xl shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Install PropertyCRM</p>
              <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                Tap <span className="inline-flex items-center px-1 mx-0.5 bg-slate-800 rounded">
                  <Share2 className="w-3 h-3 inline" />
                </span> in Safari, then "Add to Home Screen".
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-slate-400 hover:text-white p-1 flex-shrink-0"
              data-testid="ios-install-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-80 z-[60] animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt"
    >
      <div className="bg-slate-900 text-white rounded-xl shadow-2xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Install PropertyCRM</p>
          <p className="text-xs text-slate-400">Add to home screen for quick access</p>
        </div>
        <Button
          size="sm"
          className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-8 px-3 flex-shrink-0"
          onClick={handleInstall}
          data-testid="install-btn"
        >
          Install
        </Button>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-white p-1 flex-shrink-0"
          data-testid="install-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
